import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import Icon from "@/components/Icon";
import { WhatsAppButton, CallButton } from "@/components/CTAButtons";
import Differentials from "@/components/sections/Differentials";
import FaqSection from "@/components/sections/FaqSection";
import { SERVICES, IMAGES } from "@/data/site";
import { SERVICE_CONTENT } from "@/data/servicesContent";
import { whatsappUrl } from "@/lib/contact";
import { serviceSchema, breadcrumbSchema } from "@/lib/schema";
import { CheckCircle } from "@phosphor-icons/react";

export default function Servicos() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <>
      <SEO
        title="Serviços | Guincho Leve, Guincho Pesado, Munck e Auto Socorro 24h | Auto Socorro Pimenta"
        description="Conheça os serviços da Auto Socorro Pimenta: guincho leve, guincho pesado, transporte de máquinas, auto socorro 24 horas, assistência rodoviária e atendimento para seguradoras em RO e MT."
        path="/servicos"
        schemas={[
          serviceSchema("Guincho leve e pesado, munck e auto socorro 24 horas", "Serviços de reboque, resgate e içamento em Rondônia e Mato Grosso."),
          breadcrumbSchema([{ name: "Início", path: "/" }, { name: "Serviços", path: "/servicos" }]),
        ]}
      />

      <PageHeader
        eyebrow="Nossos Serviços"
        title={<>Serviços de<br /><span className="text-brand-red">resgate e reboque</span></>}
        subtitle="Do carro de passeio à carreta pesada, atendemos qualquer emergência com equipamento moderno e equipe especializada — 24 horas por dia em Rondônia e Mato Grosso."
        image={IMAGES.highwayRescue}
        trail={[{ name: "Início", path: "/" }, { name: "Serviços", path: "/servicos" }]}
      />

      {/* Quick nav */}
      <section className="border-y border-white/10 bg-brand-coal">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-x-6 gap-y-3 px-5 py-5 md:px-8">
          {SERVICES.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} className="font-heading text-xs font-medium uppercase tracking-wide text-white/60 transition-colors hover:text-brand-red">
              {s.title}
            </a>
          ))}
        </div>
      </section>

      {SERVICES.map((s, idx) => {
        const c = SERVICE_CONTENT[s.slug];
        if (!c) return null;
        const reversed = idx % 2 === 1;
        return (
          <section key={s.slug} id={s.slug} className={`scroll-mt-24 py-20 md:py-28 ${idx % 2 === 0 ? "bg-brand-obsidian" : "bg-brand-coal"}`} data-testid={`service-section-${s.slug}`}>
            <div className="mx-auto max-w-7xl px-5 md:px-8">
              <div className={`grid gap-10 lg:grid-cols-2 lg:gap-16 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <Reveal className="relative">
                  <div className="sticky top-28">
                    <img src={s.image} alt={c.title} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" />
                    <div className="mt-6 flex items-center gap-3">
                      <Icon name={s.icon} weight="light" className="h-10 w-10 text-brand-red" />
                      <span className="font-mono text-xs uppercase tracking-widest text-white/40">{String(idx + 1).padStart(2, "0")} / Serviço</span>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {c.keywords.map((k) => (
                        <span key={k} className="rounded-sm border border-white/10 px-3 py-1.5 text-xs text-white/50">{k}</span>
                      ))}
                    </div>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                      <WhatsAppButton label="Solicitar Atendimento" message={`Olá! Gostaria de solicitar o serviço de ${c.title} da Auto Socorro Pimenta.`} testid={`service-wa-${s.slug}`} size="sm" />
                      <CallButton testid={`service-call-${s.slug}`} size="sm" />
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={0.1}>
                  <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight text-white md:text-5xl">{c.title}</h2>
                  <p className="mt-4 flex items-start gap-2 text-lg leading-relaxed text-white/80">
                    <CheckCircle weight="fill" className="mt-1 h-5 w-5 shrink-0 text-brand-red" />
                    {c.lead}
                  </p>
                  <div className="mt-6 space-y-4">
                    {c.paragraphs.map((p, i) => (
                      <p key={i} className="text-sm leading-relaxed text-white/60 md:text-base">{p}</p>
                    ))}
                  </div>

                  {c.gallery && (
                    <div className="mt-8">
                      <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-red">Serviços realizados</p>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {c.gallery.map((src, gi) => (
                          <div key={gi} className="group aspect-[4/3] overflow-hidden rounded-sm border border-white/10">
                            <img src={src} alt={`${c.title} — Auto Socorro Pimenta`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      <Differentials />
      <FaqSection />
    </>
  );
}
